import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { leadsService } from '../../../services/leads.service';
import { useProduct } from '../../../contexts/ProductContext';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { Button } from '../../../ui';

interface Props {
  onLeadClick: (id: number) => void;
}

interface CalendarLead {
  id: number;
  name: string;
  nextFollowUpAt: string | null;
}

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTHS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

export function PipelineCalendar({ onLeadClick }: Props) {
  const { selectedProductId } = useProduct();
  const [cursor, setCursor] = useState(new Date());

  const { data, isLoading } = useQuery({
    queryKey: ['pipeline-cal', selectedProductId],
    queryFn: async () => {
      const result = await leadsService.list({ productId: selectedProductId, limit: 500 });
      return (result?.leads ?? result?.items ?? result ?? []) as CalendarLead[];
    },
    enabled: !!selectedProductId,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startWeekday = firstDay.getDay();

  const cells: { date: Date | null; leads: CalendarLead[] }[] = [];
  for (let i = 0; i < startWeekday; i++) {
    cells.push({ date: null, leads: [] });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const cellDate = new Date(year, month, d);
    const dayLeads = (data ?? []).filter((l) => {
      if (!l.nextFollowUpAt) {
        return false;
      }
      const due = new Date(l.nextFollowUpAt);
      return due.getFullYear() === year && due.getMonth() === month && due.getDate() === d;
    });
    cells.push({ date: cellDate, leads: dayLeads });
  }

  const today = new Date();
  const isToday = (date: Date) =>
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();

  return (
    <div className="bg-surface rounded-lg border border-border p-4">
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setCursor(new Date(year, month - 1, 1))}
          aria-label="Mês anterior"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <h2 className="font-serif text-lg text-ink-primary">
          {MONTHS[month]} {year}
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setCursor(new Date(year, month + 1, 1))}
          aria-label="Próximo mês"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-xs text-ink-tertiary mb-1">
        {WEEKDAYS.map((d) => (
          <div key={d} className="text-center font-medium py-1">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => (
          <div
            key={i}
            className={`min-h-[80px] rounded p-1 ${
              c.date ? `border ${isToday(c.date) ? 'border-accent' : 'border-border'}` : ''
            }`}
          >
            {c.date && (
              <>
                <div
                  className={`text-xs mb-1 ${isToday(c.date) ? 'text-accent font-semibold' : 'text-ink-tertiary'}`}
                >
                  {c.date.getDate()}
                </div>
                <div className="flex flex-col gap-1">
                  {c.leads.slice(0, 3).map((l) => (
                    <button
                      key={l.id}
                      onClick={() => onLeadClick(l.id)}
                      className="text-[11px] text-left bg-subtle rounded px-1 py-0.5 hover:bg-border-strong truncate text-ink-primary"
                      title={l.name}
                      type="button"
                    >
                      {l.name}
                    </button>
                  ))}
                  {c.leads.length > 3 && (
                    <span className="text-[10px] text-ink-tertiary">
                      +{c.leads.length - 3} mais
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
